/**
 * Toast — Notification toast system for DuoCast.
 * Provides a createToast() function and a ToastContainer component.
 * Toasts auto-dismiss after a configurable duration.
 */
import { type Component, createSignal, For, onCleanup } from 'solid-js';
import { IconCheck, IconWarning, IconClose } from './Icons';
import styles from './Toast.module.css';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastMessage {
  id: number;
  text: string;
  type: ToastType;
  duration: number;
}

// Global toast state
const [toasts, setToasts] = createSignal<ToastMessage[]>([]);
let nextToastId = 0;

/**
 * Show a toast notification.
 * @param text The message to display
 * @param type The visual style (success, error, info, warning)
 * @param duration Time in ms before auto-dismiss (default 3000)
 */
export function showToast(text: string, type: ToastType = 'info', duration = 3000): void {
  const id = nextToastId++;
  const toast: ToastMessage = { id, text, type, duration };

  setToasts((prev) => [...prev, toast]);

  setTimeout(() => {
    dismissToast(id);
  }, duration);
}

function dismissToast(id: number): void {
  setToasts((prev) => prev.filter((t) => t.id !== id));
}

/**
 * ToastContainer — renders active toasts.
 * Place this once at the app root level.
 */
export const ToastContainer: Component = () => {
  return (
    <div class={styles.container} role="status" aria-live="polite" aria-label="Notifications">
      <For each={toasts()}>
        {(toast) => {
          // Auto-cleanup on removal
          const [exiting, setExiting] = createSignal(false);

          const handleDismiss = () => {
            setExiting(true);
            setTimeout(() => dismissToast(toast.id), 200);
          };

          // Schedule exit animation before auto-dismiss
          const exitTimer = setTimeout(() => {
            setExiting(true);
          }, toast.duration - 200);

          onCleanup(() => clearTimeout(exitTimer));

          return (
            <div
              class={`${styles.toast} ${styles[toast.type]} ${exiting() ? styles.exiting : ''}`}
              role="alert"
            >
              <span class={styles.icon} aria-hidden="true">
                {toast.type === 'success' && <IconCheck size={18} />}
                {toast.type === 'error' && <IconWarning size={18} />}
                {toast.type === 'warning' && <IconWarning size={18} />}
                {toast.type === 'info' && <IconCheck size={18} />}
              </span>
              <span class={styles.text}>{toast.text}</span>
              <button
                class={styles.dismiss}
                onClick={handleDismiss}
                aria-label="Dismiss notification"
                type="button"
              >
                <IconClose size={14} />
              </button>
            </div>
          );
        }}
      </For>
    </div>
  );
};
